import { X, Zap, Star, Crown, QrCode, Clock, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { toast } from 'sonner';

// Cloud Function URL (same as mobile app)
const CLOUD_FUNCTION_URL = 'https://us-central1-eventfy-ff660.cloudfunctions.net/createPixPayment';

// Boost plans - EXACTLY like mobile app (basic, silver, gold)
const BOOST_PLANS = [
  {
    id: 'basic',
    name: 'Básico',
    days: 1,
    boostType: 1,
    price: 15,
    color: 'from-gray-400 to-gray-500',
    icon: Zap,
    badge: null
  },
  {
    id: 'intermediate',
    name: 'Popular',
    days: 3,
    boostType: 3,
    price: 30,
    color: 'from-slate-400 to-slate-500',
    icon: Star,
    badge: '⭐ MAIS USADO'
  },
  {
    id: 'premium',
    name: 'Premium',
    days: 7,
    boostType: 7,
    price: 50,
    color: 'from-yellow-400 to-amber-500',
    icon: Crown,
    badge: '👑 MELHOR VALOR'
  }
];

// Payment status states
const PAYMENT_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  WAITING_PAYMENT: 'waiting_payment',
  SUCCESS: 'success',
  ERROR: 'error'
};

export const BoostModal = ({ event, onClose, onBoostComplete }) => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(PAYMENT_STATUS.IDLE);
  const [pixData, setPixData] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  // ========================================
  // FIRESTORE REAL-TIME LISTENER
  // This is the ONLY source of truth
  // Path: events/{eventId}
  // Detect: boosted === true
  // Do NOT use: isHighlighted, success from backend
  // ========================================
  useEffect(() => {
    // Only start listening after PIX is generated
    if (paymentStatus !== PAYMENT_STATUS.WAITING_PAYMENT) return;

    console.log("Starting Firestore listener for event:", event.id);

    // Listen to: events/{eventId}
    const eventRef = doc(db, 'events', event.id);

    const unsubscribe = onSnapshot(eventRef, (snapshot) => {
      const data = snapshot.data();

      // DEBUG LOG - OBRIGATÓRIO
      console.log("FIRESTORE UPDATE:", data);

      if (!data) return;

      // ONLY check boosted === true
      // Do NOT use isHighlighted
      // Do NOT use success from backend
      if (data.boosted === true) {
        console.log("Payment confirmed! boosted === true");
        setPaymentStatus(PAYMENT_STATUS.SUCCESS);
        toast.success('Pagamento confirmado! Evento impulsionado com sucesso!');

        // Auto-close modal after 2 seconds
        setTimeout(() => {
          if (onBoostComplete) {
            onBoostComplete();
          }
          onClose();
        }, 2000);
      }
    }, (err) => {
      console.error("Firestore listener error:", err);
    });

    // Cleanup listener on unmount or status change
    return () => {
      console.log("Stopping Firestore listener");
      unsubscribe();
    };
  }, [paymentStatus, event.id, onBoostComplete, onClose]);

  // ========================================
  // CREATE PIX PAYMENT
  // POST to Cloud Function - do NOT validate response
  // Backend returns: { qr_code, qr_code_base64, payment_id, amount }
  // There is NO "success" field - do NOT check for it
  // ========================================
  const handleCreatePayment = async () => {
    if (!selectedPlan) return;

    // 🚨 VALIDAÇÃO DE BOOST ATIVO (COM MELHORIA)
    const now = new Date();

    const boostEnd = event?.boostEnd?.toDate
      ? event.boostEnd.toDate()
      : (event?.boostEnd ? new Date(event.boostEnd) : null);

    const isBoostActive = boostEnd ? boostEnd > now : false;

    if (isBoostActive) {
      const diasRestantes = Math.max(1, Math.ceil((boostEnd - now) / (1000 * 60 * 60 * 24)));
      toast.error(`Evento já impulsionado por mais ${diasRestantes} dia(s)`);
      return;
    }

    // 🚀 CONTINUA NORMAL
    setPaymentStatus(PAYMENT_STATUS.LOADING);
    setError(null);

    try {
      const response = await fetch(CLOUD_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          eventId: event.id,
          boostType: selectedPlan.boostType  // 1, 3, or 7
        })
      });

      const data = await response.json();

      // DEBUG LOG - OBRIGATÓRIO
      console.log("PIX RESPONSE:", data);

      // Set PIX data - do NOT validate, do NOT throw error
      // Backend format: { qr_code, qr_code_base64, payment_id, amount }
      setPixData({
        qrCode: data.qr_code_base64,      // For rendering QR image
        pixCopiaECola: data.qr_code,       // For copy/paste
        paymentId: data.payment_id,
        amount: data.amount
      });

      // Move to waiting state - Firestore listener will handle confirmation
      // Do NOT depend on payment response for success
      setPaymentStatus(PAYMENT_STATUS.WAITING_PAYMENT);

    } catch (err) {
      console.error("PIX ERROR:", err);
      setError(err.message);
      setPaymentStatus(PAYMENT_STATUS.ERROR);
      toast.error('Erro ao criar pagamento PIX');
    }
  };

  // Copy PIX code to clipboard
  const handleCopyPixCode = useCallback(() => {
    if (pixData?.pixCopiaECola) {
      navigator.clipboard.writeText(pixData.pixCopiaECola);
      setCopied(true);
      toast.success('Código PIX copiado!');
      setTimeout(() => setCopied(false), 3000);
    }
  }, [pixData]);

  // ========================================
  // RENDER: Plan Cards (scrollable area only)
  // CTA button lives in the fixed footer below
  // ========================================
  const renderPlanCards = () => (
    <div className="p-4 sm:p-6">
      <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Escolha seu plano:</h3>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        {BOOST_PLANS.map((plan) => {
          const Icon = plan.icon;
          const isSelected = selectedPlan?.id === plan.id;

          return (
            <div
              key={plan.id}
              onClick={() => setSelectedPlan(plan)}
              className={`
                relative cursor-pointer rounded-xl border-2 p-4 sm:p-6 transition-all duration-200
                ${plan.badge ? 'mt-3 sm:mt-0 pt-5 sm:pt-6' : ''}
                ${isSelected
                  ? 'border-primary shadow-lg sm:scale-105 bg-primary/[0.03]'
                  : 'border-border hover:border-primary/50 hover:shadow-md'
                }
              `}
              data-testid={`boost-plan-${plan.id}`}
            >
              {plan.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-primary-foreground rounded-full text-xs font-bold whitespace-nowrap">
                  {plan.badge}
                </div>
              )}

              {/* Top row — always visible on all screen sizes */}
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 shrink-0 rounded-lg bg-gradient-to-br ${plan.color} flex items-center justify-center`}>
                  <Icon className="w-4 h-4 text-white" />
                </div>

                <div className="flex-1 flex items-center justify-between min-w-0">
                  <h4 className="text-base font-bold truncate">{plan.name}</h4>
                  <div className="flex items-baseline gap-1.5 shrink-0 ml-2">
                    <span className="text-sm font-semibold text-muted-foreground">
                      {plan.days} {plan.days === 1 ? 'dia' : 'dias'}
                    </span>
                    <span className="text-base font-bold text-primary">
                      R$ {plan.price}
                    </span>
                  </div>
                </div>
              </div>

              {/* "Selecionado" indicator — appears instantly on click */}
              {isSelected && (
                <div className="mt-2 flex items-center gap-1.5 text-primary text-xs font-semibold">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                  Selecionado
                </div>
              )}

              {/* Expandable benefits — animates open only for selected card */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateRows: isSelected ? '1fr' : '0fr',
                  transition: 'grid-template-rows 280ms ease'
                }}
              >
                <div style={{ overflow: 'hidden', minHeight: 0 }}>
                  <div className="border-t border-primary/20 mt-3 pt-3">
                    <ul className="space-y-1.5">
                      <li className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
                        <span className="text-primary font-bold text-xs leading-none">✓</span>
                        Destaque visual no app
                      </li>
                      <li className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
                        <span className="text-primary font-bold text-xs leading-none">✓</span>
                        Aparece primeiro nas buscas
                      </li>
                      <li className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
                        <span className="text-primary font-bold text-xs leading-none">✓</span>
                        {plan.days} {plan.days === 1 ? 'dia' : 'dias'} de impulsionamento
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
        <p className="text-xs sm:text-sm text-blue-900 dark:text-blue-100">
          <strong>💡 Dica:</strong> Eventos impulsionados aparecem em destaque no app mobile e têm{' '}
          <strong>até 5x mais visualizações</strong> que eventos normais!
        </p>
      </div>
    </div>
  );

  // ========================================
  // RENDER: PIX QR Code
  // ========================================
  const renderPixPayment = () => (
    <div className="p-4 sm:p-6">
      <div className="text-center mb-4 sm:mb-6">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 rounded-full mb-3 sm:mb-4">
          <Clock className="w-4 h-4 animate-pulse" />
          <span className="font-medium text-sm">Aguardando pagamento</span>
        </div>
        <h3 className="text-lg sm:text-xl font-bold mb-2">Escaneie o QR Code para pagar</h3>
        <p className="text-sm text-muted-foreground">
          Plano {selectedPlan?.name} - {selectedPlan?.days} {selectedPlan?.days === 1 ? 'dia' : 'dias'} por R$ {selectedPlan?.price}
        </p>
      </div>

      {/* QR Code */}
      <div className="flex justify-center mb-4 sm:mb-6">
        <div className="bg-white p-3 sm:p-4 rounded-xl shadow-lg">
          {pixData?.qrCode ? (
            <img
              src={pixData.qrCode.startsWith('data:') ? pixData.qrCode : `data:image/png;base64,${pixData.qrCode}`}
              alt="QR Code PIX"
              className="w-48 h-48 sm:w-64 sm:h-64"
              data-testid="pix-qr-code"
            />
          ) : (
            <div className="w-48 h-48 sm:w-64 sm:h-64 flex items-center justify-center bg-gray-100 rounded">
              <p className="text-gray-500 text-sm text-center px-4">
                QR Code não disponível.<br/>
                Use o código PIX abaixo.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* PIX Copia e Cola */}
      {pixData?.pixCopiaECola && (
        <div className="mb-4 sm:mb-6">
          <p className="text-sm text-muted-foreground text-center mb-2">
            Ou copie o código PIX:
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              value={pixData.pixCopiaECola}
              readOnly
              className="flex-1 px-3 py-2 text-xs bg-muted border border-border rounded-lg font-mono truncate"
              data-testid="pix-copy-paste-code"
            />
            <Button
              variant={copied ? "default" : "outline"}
              onClick={handleCopyPixCode}
              className="shrink-0"
              data-testid="copy-pix-btn"
            >
              {copied ? 'Copiado!' : 'Copiar'}
            </Button>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-muted/50 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
        <h4 className="font-semibold mb-2 text-sm sm:text-base">Como pagar:</h4>
        <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
          <li>Abra o app do seu banco</li>
          <li>Escolha pagar com PIX</li>
          <li>Escaneie o QR Code ou cole o código</li>
          <li>Confirme o pagamento</li>
        </ol>
      </div>

      {/* Auto-update notice */}
      <div className="p-3 sm:p-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg mb-4 sm:mb-6">
        <p className="text-xs sm:text-sm text-green-900 dark:text-green-100 text-center">
          <strong>✨ Esta tela atualizará automaticamente</strong> assim que o pagamento for confirmado.
        </p>
      </div>

      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={() => {
            setPaymentStatus(PAYMENT_STATUS.IDLE);
            setPixData(null);
          }}
          className="flex-1"
        >
          Voltar
        </Button>
        <Button
          variant="outline"
          onClick={onClose}
        >
          Fechar
        </Button>
      </div>
    </div>
  );

  // ========================================
  // RENDER: Success State
  // ========================================
  const renderSuccess = () => (
    <div className="p-6 text-center">
      <div className="w-20 h-20 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
        <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
      </div>
      <h3 className="text-2xl font-bold mb-2">Pagamento Confirmado!</h3>
      <p className="text-muted-foreground mb-4">
        Seu evento foi impulsionado com sucesso{selectedPlan ? ` por ${selectedPlan.days} ${selectedPlan.days === 1 ? 'dia' : 'dias'}` : ''}.
      </p>
      <p className="text-sm text-muted-foreground mb-6">
        Esta janela fechará automaticamente...
      </p>
      <Button onClick={onClose} size="lg" className="gap-2">
        <Zap className="w-4 h-4" />
        Fechar agora
      </Button>
    </div>
  );

  // ========================================
  // RENDER: Error State
  // ========================================
  const renderError = () => (
    <div className="p-6 text-center">
      <div className="w-20 h-20 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center mx-auto mb-6">
        <AlertCircle className="w-10 h-10 text-red-600 dark:text-red-400" />
      </div>
      <h3 className="text-2xl font-bold mb-2">Erro ao criar pagamento</h3>
      <p className="text-muted-foreground mb-6">
        {error || 'Ocorreu um erro ao processar o pagamento. Tente novamente.'}
      </p>
      <div className="flex gap-3 justify-center">
        <Button
          onClick={() => {
            setPaymentStatus(PAYMENT_STATUS.IDLE);
            setError(null);
          }}
          variant="outline"
        >
          Tentar novamente
        </Button>
        <Button onClick={onClose}>
          Fechar
        </Button>
      </div>
    </div>
  );

  const isPlanSelection = paymentStatus === PAYMENT_STATUS.IDLE || paymentStatus === PAYMENT_STATUS.LOADING;

  // ========================================
  // MAIN RENDER
  // ========================================
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-t-2xl sm:rounded-xl max-w-4xl w-full h-[88vh] sm:h-auto sm:max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="boost-modal"
      >
        {/* Drag handle — mobile only visual cue */}
        <div className="sm:hidden flex justify-center pt-3 pb-1 shrink-0">
          <div className="w-10 h-1 bg-muted-foreground/30 rounded-full" />
        </div>

        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-4 sm:p-6 flex items-center justify-between shrink-0 z-10">
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
              Impulsionar Evento
            </h2>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">
              {paymentStatus === PAYMENT_STATUS.WAITING_PAYMENT
                ? 'Realize o pagamento via PIX'
                : 'Destaque seu evento e aumente sua visibilidade'
              }
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-boost-modal">
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Event Info */}
        <div className="p-4 sm:p-6 border-b border-border bg-muted/30 shrink-0">
          <p className="text-xs sm:text-sm text-muted-foreground mb-0.5">Evento selecionado:</p>
          <p className="font-semibold text-sm sm:text-lg leading-tight">{event.title}</p>
          <p className="text-xs sm:text-sm text-muted-foreground">{event.category} • {event.city}</p>
        </div>

        {/* Scrollable content area — only this section scrolls */}
        <div className="flex-1 overflow-y-auto">
          {isPlanSelection && renderPlanCards()}
          {paymentStatus === PAYMENT_STATUS.WAITING_PAYMENT && renderPixPayment()}
          {paymentStatus === PAYMENT_STATUS.SUCCESS && renderSuccess()}
          {paymentStatus === PAYMENT_STATUS.ERROR && renderError()}
        </div>

        {/* Fixed CTA footer — visible only during plan selection, always above the fold */}
        {isPlanSelection && (
          <div className="shrink-0 border-t border-border bg-card px-4 sm:px-6 py-4">
            <div className="flex gap-3">
              <Button
                onClick={handleCreatePayment}
                disabled={!selectedPlan || paymentStatus === PAYMENT_STATUS.LOADING}
                className="flex-1 gap-2"
                size="lg"
                data-testid="generate-pix-btn"
              >
                {paymentStatus === PAYMENT_STATUS.LOADING ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Gerando PIX...
                  </>
                ) : (
                  <>
                    <QrCode className="w-4 h-4" />
                    {selectedPlan ? `Gerar PIX — R$ ${selectedPlan.price}` : 'Selecione um plano'}
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={onClose}
                size="lg"
              >
                Cancelar
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
